import type { CityLine } from "../../../api/cityOrders";
import { formatMoney, formatQuantity } from "../../../api/money";
import type { Choice, Choices } from "./cityOrderReview";

interface Props {
  lines: CityLine[];
  choices: Choices;
  onChoose: (code: string, choice: Choice | null) => void;
  /** The Clear step: each difference takes a decision. */
  editable: boolean;
}

const STATE_LABEL: Record<CityLine["state"], string> = { matched: "Matches", differs: "Differs", city_only: "City only", ours_only: "Ours only" };

function cityCell(line: CityLine): string {
  const city = line.city;
  if (city === null) {
    return "—";
  }
  if (line.ours?.stated) {
    return formatMoney(city.amount);
  }
  const rate = city.rate === null ? city.rates.map((each) => formatMoney(each)).join(" / ") : formatMoney(city.rate);
  return `${formatQuantity(city.qty)} ${city.uom} × ${rate} = ${formatMoney(city.amount)}`;
}

function oursCell(line: CityLine): string {
  const ours = line.ours;
  if (ours === null) {
    return "—";
  }
  if (ours.stated || ours.rate === null) {
    return formatMoney(ours.amount);
  }
  return `${formatQuantity(ours.qty)} ${ours.unit ?? ""} × ${formatMoney(ours.rate)} = ${formatMoney(ours.amount)}`;
}

/** The City's PO and our BOQ side by side, code by code. A difference shows
 * what is different in a sentence, and -- on the Clear step -- asks which
 * figure stands. Keeping ours asks why. */
export default function CheckTable({ lines, choices, onChoose, editable }: Props) {
  return (
    <div className="table-card">
      <table className="data-table qr-lines" aria-label="The City's PO against our BOQ">
        <thead>
          <tr>
            <th>Code</th>
            <th>Line</th>
            <th className="num">The City's PO</th>
            <th className="num">Our BOQ</th>
            <th>Check</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const differs = line.state !== "matched";
            const choice = choices[line.code];
            return (
              <tr key={line.code} className={`qr-row ${differs ? (choice ? "resolved" : "blocking") : ""}`.trim()} data-testid={`city-line-${line.code}`}>
                <td className="mono">{line.code}</td>
                <td>
                  <div>{line.description}</div>
                  {differs ? <span className="qr-line-note blocking">{line.message}</span> : null}
                  {differs && editable ? <Decide line={line} choice={choice} onChoose={onChoose} /> : null}
                </td>
                <td className="num">{cityCell(line)}</td>
                <td className="num">{oursCell(line)}</td>
                <td>{STATE_LABEL[line.state]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Decide({ line, choice, onChoose }: { line: CityLine; choice: Choice | undefined; onChoose: (code: string, choice: Choice | null) => void }) {
  const name = `decide-${line.code}`;
  return (
    <fieldset className="qr-decide" aria-label={`Decide on ${line.code}`}>
      <label>
        <input
          type="radio"
          name={name}
          checked={choice?.keep === "city"}
          disabled={!line.can_use_city}
          onChange={() => onChoose(line.code, { keep: "city", reason: "" })}
        />{" "}
        Use the City's figure
      </label>
      <label>
        <input type="radio" name={name} checked={choice?.keep === "ours"} onChange={() => onChoose(line.code, { keep: "ours", reason: choice?.reason ?? "" })} /> Keep
        ours
      </label>
      {choice?.keep === "ours" ? (
        <label className="move-note">
          Why does our figure stand for {line.code}?
          <textarea value={choice.reason} rows={2} maxLength={500} required onChange={(event) => onChoose(line.code, { keep: "ours", reason: event.target.value })} />
        </label>
      ) : null}
    </fieldset>
  );
}
